import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import React, { ReactElement, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { ThemeProvider } from "styled-components";

import Section from "components/Section";
import Slider from "components/Slider/Slider";
import TextField from "components/TextField/TextField";
import { useDebounce } from "hooks";
import replacePlaceholders from "utils/replacePlaceholders";

import {
    Container,
    NotificationWrapper,
    NotificationText,
    ResultInner,
    Heading,
    Introduction,
    ResultText,
    ResultContent,
    FieldWrapper,
    CheckIcon,
    SubHeading,
    TotalSavingAmount,
    TotalSavingAmountDescription,
    MonthlySavingAmount,
    ShowResultButton,
    Header,
    Calculator,
    ResultItem,
    ResultLabel,
    ResultInnerBottom,
    ResultInnerTotal,
} from "./SalaryExchange.styled";
import SalaryExchangeProps from "./SalaryExchangeProps";

const SalaryExchange = ({
    anchorName,
    heading,
    resultContent,
    introduction,
    identifier,
    theme,
    actionTheme,
    contentTheme,
    minSalary,
    minSavings,
    employerContribution,
    interestRate,
    defaultAge,
    defaultPensionAge,
    localization,
}: SalaryExchangeProps): ReactElement => {
    // GTM
    const sendDataToGTM = useGTMDispatch();

    // Form Control
    const { control } = useForm();

    // Return value if not array of number
    const getNumberValue = (value: number | number[]): number => {
        return Array.isArray(value) ? value[0] : value;
    };

    // Defined values
    const interestRateValue = interestRate === undefined ? 0 : interestRate;
    const employerContributionValue =
        employerContribution === undefined ? 0 : employerContribution;
    const minSavingsValue = minSavings === undefined ? 0 : minSavings;
    const maxSavingValue = 20000;
    const minSalaryPresetValue = minSalary === undefined ? 0 : minSalary;
    const minSalaryValue = minSalaryPresetValue + minSavingsValue;

    // Count monthly interest rate
    const monthlyInterestRate = Math.pow(1 + interestRateValue, 1 / 12) - 1; //~ 0.49%

    // State Hooks
    const [salaryValue, setSalaryValue] = useState<number | number[]>(0);
    const [savingValue, setSavingValue] = useState<number | number[]>(0);
    const [currentAge, setCurrentAge] = useState<number | number[]>(
        defaultAge ?? 1,
    );
    const [pensionAge, setPensionAge] = useState<number | number[]>(
        defaultPensionAge ?? 1,
    );
    const [showResult, setShowResult] = useState<boolean>(false);
    const [showFinalResult, setShowFinalResult] = useState<boolean>(false);

    // Debounced Values
    const debouncedSalaryValue = useDebounce(salaryValue, 1000);
    const debouncedSavingValue = useDebounce(savingValue, 1000);
    const debouncedCurrentAge = useDebounce(currentAge, 1000);
    const debouncedPensionAge = useDebounce(pensionAge, 1000);
    const debouncedShowResult = useDebounce(showResult, 1000);
    const debouncedShowFinalResult = useDebounce(showFinalResult, 1000);

    // Derived States
    const calculatedSavingLimit =
        getNumberValue(debouncedSalaryValue) - minSalaryValue + minSavingsValue;
    const salaryMinValueReached =
        getNumberValue(debouncedSalaryValue) >= minSalaryValue;
    const savingMaxValueReached =
        getNumberValue(debouncedSavingValue) > calculatedSavingLimit;
    const isCalculatedSavingAboveMaxLimit =
        calculatedSavingLimit > maxSavingValue;

    const monthlySavingsAfterContribution =
        getNumberValue(savingValue) * (1 + employerContributionValue);

    // To price strings
    const maxSavingValueToPrice = `${maxSavingValue.toLocaleString("sv-SE")} ${localization?.currencyCode}`;
    const calculatedSavingLimitToPrice = `${calculatedSavingLimit.toLocaleString("sv-SE")} ${localization?.currencyCode}`;
    const monthlySavingsAfterContributionToPrice = `${Number(monthlySavingsAfterContribution.toFixed(0)).toLocaleString("sv-SE")} ${localization?.currencyCode}`;

    const [savingYears, setSavingYears] = useState<number>(0);

    const handleShowFinalResult = () => {
        setShowFinalResult(true);
    };

    // Months of saving years
    const savingMonths = savingYears * 12;

    let totalSaving = 0;

    // Calculate each month increment
    for (let i = 0; i < savingMonths; i++) {
        if (monthlySavingsAfterContribution) {
            // Add this month's contribution
            totalSaving += monthlySavingsAfterContribution;

            // Apply the interest to the total accumulated savings
            totalSaving *= 1 + monthlyInterestRate;
        }
    }

    useEffect(() => {
        setShowResult(
            getNumberValue(debouncedSavingValue) > 0 &&
                getNumberValue(debouncedSalaryValue) > 0,
        );
    }, [debouncedSavingValue, debouncedSalaryValue]);

    useEffect(() => {
        setSavingYears(
            getNumberValue(debouncedPensionAge) -
                getNumberValue(debouncedCurrentAge),
        );
    }, [debouncedPensionAge, debouncedCurrentAge]);

    // Handler Functions
    const handleSalaryInputChange = (
        event: Event | React.ChangeEvent | undefined,
        value: number | string,
    ) => {
        const numericValue =
            typeof value === "string" && value.trim() === ""
                ? null
                : typeof value === "string"
                  ? parseFloat(value.replace(/\s+/g, ""))
                  : value;

        if (numericValue !== null && !isNaN(numericValue)) {
            setSalaryValue(numericValue);
        } else {
            setSalaryValue(0);
        }
    };

    const handleSavingSliderChange = (
        event: Event | React.ChangeEvent | undefined,
        value: number | number[],
    ) => {
        setSavingValue(value);
    };

    const handleCurrentAgeSliderChange = (
        event: Event | React.ChangeEvent | undefined,
        value: number | number[],
    ) => {
        setCurrentAge(value);
    };

    const handlePensionAgeSliderChange = (
        event: Event | React.ChangeEvent | undefined,
        value: number | number[],
    ) => {
        setPensionAge(value);
    };

    const StepOneResultHeading =
        salaryMinValueReached && savingMaxValueReached
            ? localization?.resultHeadingWarning
            : salaryMinValueReached && !savingMaxValueReached
              ? localization?.resultHeadingSuccess
              : localization?.resultHeadingError;

    // Replace placeholders in result text based on conditions
    const StepOneResultText =
        salaryMinValueReached && savingMaxValueReached
            ? replacePlaceholders(localization?.resultTextWarning, {
                  calculatedSavingLimit:
                      calculatedSavingLimit.toLocaleString("sv-SE"),
                  currencyCode: localization?.currencyCode || "{currencyCode}",
              })
            : salaryMinValueReached && !savingMaxValueReached
              ? localization?.resultTextSuccess
              : replacePlaceholders(localization?.resultTextError, {
                    minSalary: minSalaryValue.toLocaleString("sv-SE"),
                    currencyCode:
                        localization?.currencyCode || "{currencyCode}",
                });

    const showStepTwoButton = salaryMinValueReached && !savingMaxValueReached;

    // GTM tracking
    useEffect(() => {
        sendDataToGTM({
            event: "salary_exchange",
            step: showFinalResult ? "3" : showResult ? "2" : "1",
        });
    }, [showResult, showFinalResult, sendDataToGTM]);

    return (
        <ThemeProvider theme={{ theme, actionTheme, contentTheme }}>
            <Section theme={theme} alignItems="center">
                <Container key={identifier} id={anchorName}>
                    <Header>
                        {heading && <Heading>{heading}</Heading>}
                        {introduction && (
                            <Introduction>{introduction}</Introduction>
                        )}
                    </Header>
                    <Calculator>
                        {/* Step 1 */}
                        <FieldWrapper>
                            <TextField
                                endAdornment={
                                    salaryMinValueReached && (
                                        <CheckIcon icon="check28" />
                                    )
                                }
                                label={localization?.monthlySalaryLabel}
                                id={`MonthlySalary-${identifier}`}
                                elementName="MonthlySalary"
                                control={control}
                                size="small"
                                inputMode="numeric"
                                inputFormatNumber={true}
                                error={true}
                                onChange={handleSalaryInputChange}
                            />
                        </FieldWrapper>
                        <NotificationWrapper aria-live="polite">
                            {!salaryMinValueReached &&
                                typeof debouncedSalaryValue === "number" &&
                                !Array.isArray(debouncedSalaryValue) &&
                                debouncedSalaryValue > 0 && (
                                    <NotificationText
                                        theme={{
                                            theme: theme,
                                            error: true,
                                        }}
                                    >
                                        {replacePlaceholders(
                                            localization?.recommendedSalaryText,
                                            {
                                                minSalary:
                                                    minSalaryValue.toLocaleString(
                                                        "sv-SE",
                                                    ),
                                                currencyCode:
                                                    localization?.currencyCode ||
                                                    "{currencyCode}",
                                            },
                                        )}
                                    </NotificationText>
                                )}
                        </NotificationWrapper>
                        <FieldWrapper>
                            <Slider
                                predefinedValueNumber={0}
                                adornment={localization?.currencyCode}
                                label={localization?.monthlyExchangeLabel}
                                id={`MonthlyExchange-${identifier}`}
                                elementName="MonthlyExchange"
                                input={true}
                                max={maxSavingValue}
                                min={0}
                                control={control}
                                step={500}
                                valueLabelDisplay="on"
                                size="small"
                                onChange={handleSavingSliderChange}
                            />
                        </FieldWrapper>
                        <NotificationWrapper aria-live="polite">
                            {salaryMinValueReached &&
                                !isCalculatedSavingAboveMaxLimit && (
                                    <NotificationText
                                        theme={{
                                            theme: theme,
                                            error: savingMaxValueReached,
                                        }}
                                    >
                                        {localization?.possibleSavingAmountText}{" "}
                                        <span translate="no">
                                            {calculatedSavingLimitToPrice}
                                        </span>
                                        {"."}
                                    </NotificationText>
                                )}
                            {salaryMinValueReached &&
                                isCalculatedSavingAboveMaxLimit && (
                                    <NotificationText>
                                        {localization?.possibleSavingAmountText}{" "}
                                        <span translate="no">
                                            {calculatedSavingLimitToPrice}
                                        </span>
                                        {", "}
                                        {
                                            localization?.calculatedSavingAboveMaxLimitText
                                        }{" "}
                                        <span translate="no">
                                            {maxSavingValueToPrice}
                                        </span>
                                        {"."}
                                    </NotificationText>
                                )}
                        </NotificationWrapper>
                        {/* Step 1 Results */}
                        <ResultItem
                            aria-live={
                                debouncedShowResult && !debouncedShowFinalResult
                                    ? "off"
                                    : "polite"
                            }
                        >
                            {showResult && !showFinalResult && (
                                <ResultInner>
                                    <>
                                        <div aria-live="polite">
                                            <SubHeading>
                                                {StepOneResultHeading}
                                            </SubHeading>
                                            <ResultText>
                                                {StepOneResultText}
                                            </ResultText>
                                        </div>
                                        {showStepTwoButton && (
                                            <ShowResultButton
                                                variant="primary"
                                                onClick={handleShowFinalResult}
                                            >
                                                {
                                                    localization?.resultCtaButtonText
                                                }
                                            </ShowResultButton>
                                        )}
                                    </>
                                </ResultInner>
                            )}
                        </ResultItem>
                        {/* Step 2 */}
                        <ResultItem>
                            {showFinalResult && (
                                <>
                                    <FieldWrapper>
                                        <Slider
                                            predefinedValueNumber={defaultAge}
                                            adornment={localization?.year}
                                            label={localization?.ageLabel}
                                            id={`CurrentAge-${identifier}`}
                                            elementName="CurrentAge"
                                            input={true}
                                            max={67}
                                            min={25}
                                            control={control}
                                            step={1}
                                            valueLabelDisplay="on"
                                            size="small"
                                            onChange={
                                                handleCurrentAgeSliderChange
                                            }
                                        />
                                    </FieldWrapper>
                                    <FieldWrapper>
                                        <Slider
                                            predefinedValueNumber={
                                                defaultPensionAge
                                            }
                                            adornment={localization?.year}
                                            label={
                                                localization?.pensionAgeLabel
                                            }
                                            id={`PensionAge-${identifier}`}
                                            elementName="PensionAge"
                                            input={true}
                                            max={85}
                                            min={55}
                                            control={control}
                                            step={1}
                                            valueLabelDisplay="on"
                                            size="small"
                                            onChange={
                                                handlePensionAgeSliderChange
                                            }
                                        />
                                    </FieldWrapper>
                                </>
                            )}
                        </ResultItem>
                        {/* Step 2 Results */}
                        <ResultItem
                            aria-live={
                                debouncedShowFinalResult ? "off" : "polite"
                            }
                        >
                            {showFinalResult && (
                                <>
                                    <ResultInnerTotal aria-live="polite">
                                        <ThemeProvider
                                            theme={{
                                                contentTheme: "blue",
                                            }}
                                        >
                                            <TotalSavingAmount>
                                                <span translate="no">
                                                    {Number(
                                                        totalSaving.toFixed(0),
                                                    ).toLocaleString("sv-SE")}
                                                </span>{" "}
                                                {localization?.currencyCode}
                                            </TotalSavingAmount>
                                            <TotalSavingAmountDescription>
                                                {replacePlaceholders(
                                                    localization?.resultTotalSavingAmountDescription,
                                                    {
                                                        savingYears:
                                                            savingYears,
                                                    },
                                                )}
                                            </TotalSavingAmountDescription>
                                        </ThemeProvider>
                                    </ResultInnerTotal>
                                    <ResultInner>
                                        <div aria-live="polite">
                                            <ResultLabel>
                                                {localization?.resultLabelText}
                                            </ResultLabel>
                                            <MonthlySavingAmount>
                                                {
                                                    monthlySavingsAfterContributionToPrice
                                                }
                                            </MonthlySavingAmount>
                                        </div>
                                        <ResultText>
                                            {replacePlaceholders(
                                                localization?.resultContributionInfoText,
                                                {
                                                    employerContribution:
                                                        (
                                                            employerContributionValue *
                                                            100
                                                        ).toFixed(1) + "%",
                                                },
                                            )}
                                        </ResultText>
                                        <ResultText>
                                            {replacePlaceholders(
                                                localization?.resultCalculationDescription,
                                                {
                                                    interestRate:
                                                        (
                                                            interestRateValue *
                                                            100
                                                        ).toFixed(0) + "%",
                                                },
                                            )}
                                        </ResultText>
                                    </ResultInner>
                                    <ResultInnerBottom>
                                        <ResultText>
                                            {
                                                localization?.resultBottomInformation
                                            }
                                        </ResultText>
                                        {resultContent && (
                                            <ResultContent
                                                editPropertyName="ResultContent"
                                                content={resultContent}
                                            />
                                        )}
                                    </ResultInnerBottom>
                                </>
                            )}
                        </ResultItem>
                    </Calculator>
                </Container>
            </Section>
        </ThemeProvider>
    );
};

export default React.memo(SalaryExchange);
